import { useState } from 'react';

interface FAQProps {
  language: 'pt' | 'en';
}

const content = {
  pt: {
    eyebrow: 'Perguntas Frequentes',
    title: 'O que você PRECISA saber',
    subtitle: 'Respostas objetivas sobre consórcio, estrutura e processo.',
    faqs: [
      {
        question: 'O que é um consórcio e como ele funciona?',
        answer:
          'Consórcio é uma modalidade de compra coletiva regulamentada pelo Banco Central do Brasil. Um grupo de pessoas contribui mensalmente com parcelas para formar um fundo comum. Periodicamente, um ou mais participantes são contemplados — por sorteio ou lance — e recebem a carta de crédito para adquirir o bem desejado. Não há cobrança de juros, apenas taxa de administração.',
      },
      {
        question: 'Qual a diferença entre consórcio e financiamento?',
        answer:
          'No financiamento, você recebe o bem imediatamente e paga juros sobre o valor total ao longo do contrato — o custo final é significativamente maior. No consórcio, não há juros: você paga apenas a taxa de administração e aguarda a contemplação. É uma estratégia para quem planeja com antecedência e busca eficiência de custo no médio e longo prazo.',
      },
      {
        question: 'A contemplação pode ser garantida?',
        answer:
          'Não. A FGT Capital não garante contemplação em prazo determinado. A contemplação ocorre por sorteio mensal ou por lance — estratégia que pode antecipar o acesso à carta de crédito, mas que depende da dinâmica do grupo e do regulamento da administradora. Qualquer promessa de contemplação garantida é vedada pela regulação do Banco Central.',
      },
      {
        question: 'O que é uma carta contemplada e como avaliá-la?',
        answer:
          'Carta contemplada é aquela cujo titular já foi sorteado ou teve seu lance aceito, porém não deseja utilizar o crédito. Pode ser adquirida de terceiros no mercado secundário. A avaliação técnica envolve análise do regulamento do grupo, saldo devedor, taxa de administração remanescente, restrições de uso e viabilidade jurídica — etapas que a FGT Capital conduz no diagnóstico estratégico.',
      },
      {
        question: 'Quais tipos de bens podem ser adquiridos via consórcio?',
        answer:
          'Imóveis residenciais e comerciais, terrenos, construção e reforma, veículos leves e pesados, máquinas e equipamentos, e serviços em geral. O consórcio imobiliário é especialmente relevante para aquisição, construção, ampliação ou quitação de financiamento existente.',
      },
      {
        question: 'Como funciona o lance e quais são as estratégias?',
        answer:
          'O lance é uma oferta adicional feita pelo consorciado para antecipar a contemplação. Pode ser lance livre (valor definido pelo participante), lance fixo (percentual pré-determinado pelo grupo) ou lance embutido (utiliza parte do próprio crédito). A estratégia de lance ideal depende do saldo do grupo, do histórico de contemplações e do objetivo do consorciado — análise que compõe o diagnóstico da FGT Capital.',
      },
      {
        question: 'Quais são os custos envolvidos em um consórcio?',
        answer:
          'Os custos principais são: taxa de administração (remuneração da administradora, diluída nas parcelas), fundo de reserva (proteção do grupo contra inadimplência) e, em alguns grupos, seguro de vida. Não há IOF nem juros. O custo efetivo total (CET) é substancialmente inferior ao de um financiamento convencional para o mesmo bem.',
      },
      {
        question: 'O consórcio é seguro? Quem regula?',
        answer:
          'Sim. O sistema de consórcios no Brasil é regulamentado e fiscalizado pelo Banco Central do Brasil (Bacen), com base na Lei nº 11.795/2008. As administradoras precisam de autorização para operar. O fundo do grupo é segregado do patrimônio da administradora, o que protege os consorciados em caso de insolvência da empresa.',
      },
      {
        question: 'O que acontece se eu não conseguir pagar as parcelas?',
        answer:
          'A inadimplência suspende o direito à contemplação e pode resultar em exclusão do grupo, com devolução dos valores pagos — descontadas as taxas previstas em contrato — ao término do grupo ou em assembleia específica. Por isso, a análise de capacidade financeira e horizonte de comprometimento é parte essencial do diagnóstico estratégico da FGT Capital.',
      },
      {
        question: 'Como o diagnóstico da FGT Capital funciona na prática?',
        answer:
          'O processo tem três etapas: (1) você envia as informações essenciais pelo formulário; (2) a equipe FGT realiza uma análise preliminar do seu objetivo, capacidade financeira e cenários disponíveis; (3) realizamos uma reunião estratégica de 30 a 45 minutos para apresentar a leitura estrutural e os próximos passos. Sem compromisso de contratação. Sem promessas.',
      },
    ],
  },
  en: {
    eyebrow: 'Frequently Asked Questions',
    title: 'What you NEED to know',
    subtitle: 'Objective answers about consortium, structure and process.',
    faqs: [
      {
        question: 'What is a consortium and how does it work?',
        answer:
          'A consortium is a collective purchasing model regulated by the Central Bank of Brazil. A group of people contributes monthly installments to form a common fund. Periodically, one or more participants are awarded — by draw or bid — and receive a credit letter to acquire the desired asset. There are no interest charges, only an administration fee.',
      },
      {
        question: 'What is the difference between a consortium and financing?',
        answer:
          'With financing, you receive the asset immediately but pay interest on the total amount throughout the contract — the final cost is significantly higher. With a consortium, there are no interest charges: you pay only the administration fee and wait for the award. It is a strategy for those who plan ahead and seek cost efficiency in the medium and long term.',
      },
      {
        question: 'Can an award be guaranteed?',
        answer:
          'No. FGT Capital does not guarantee an award within a specific timeframe. Awards occur through monthly draws or bids — a strategy that can accelerate access to the credit letter, but depends on group dynamics and the administrator\'s regulations. Any promise of guaranteed award is prohibited by Central Bank regulations.',
      },
      {
        question: 'What is an awarded letter and how is it evaluated?',
        answer:
          'An awarded letter is one whose holder has already been drawn or had their bid accepted, but does not wish to use the credit. It can be acquired from third parties on the secondary market. Technical evaluation involves analysis of group regulations, outstanding balance, remaining administration fee, usage restrictions and legal feasibility — steps that FGT Capital conducts in the strategic assessment.',
      },
      {
        question: 'What types of assets can be acquired via consortium?',
        answer:
          'Residential and commercial real estate, land, construction and renovation, light and heavy vehicles, machinery and equipment, and general services. Real estate consortium is especially relevant for acquisition, construction, expansion or settlement of existing financing.',
      },
      {
        question: 'How does bidding work and what are the strategies?',
        answer:
          'A bid is an additional offer made by the consortium member to advance the award. It can be a free bid (amount defined by the participant), fixed bid (predetermined percentage by the group) or embedded bid (uses part of the credit itself). The ideal bid strategy depends on the group balance, award history and the member\'s objective — analysis that forms part of FGT Capital\'s assessment.',
      },
      {
        question: 'What are the costs involved in a consortium?',
        answer:
          'The main costs are: administration fee (administrator\'s remuneration, spread across installments), reserve fund (group protection against default) and, in some groups, life insurance. There is no IOF or interest. The total effective cost (TEC) is substantially lower than conventional financing for the same asset.',
      },
      {
        question: 'Is the consortium safe? Who regulates it?',
        answer:
          'Yes. The consortium system in Brazil is regulated and supervised by the Central Bank of Brazil (Bacen), based on Law No. 11,795/2008. Administrators need authorization to operate. The group fund is segregated from the administrator\'s assets, protecting consortium members in case of company insolvency.',
      },
      {
        question: 'What happens if I cannot pay the installments?',
        answer:
          'Default suspends the right to an award and may result in exclusion from the group, with a refund of amounts paid — minus contractual fees — at the end of the group or at a specific assembly. This is why financial capacity analysis and commitment horizon are essential parts of FGT Capital\'s strategic assessment.',
      },
      {
        question: 'How does FGT Capital\'s assessment work in practice?',
        answer:
          'The process has three stages: (1) you submit essential information via the form; (2) the FGT team performs a preliminary analysis of your objective, financial capacity and available scenarios; (3) we hold a 30 to 45-minute strategic meeting to present the structural reading and next steps. No hiring commitment. No promises.',
      },
    ],
  },
};

export default function FAQ({ language }: FAQProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const t = content[language];

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="bg-[#FBF8F3] border-b border-[#E7E1D9]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        {/* Header */}
        <div className="max-w-2xl mb-16">
          <h2 className="text-sm uppercase tracking-widest text-[#2E2E2E] mb-6 font-semibold">
            {t.eyebrow}
          </h2>
          <p className="text-4xl lg:text-5xl font-semibold text-[#0B0B0B] leading-tight mb-4">
            {t.title}
          </p>
          <p className="text-lg text-[#2E2E2E]">{t.subtitle}</p>
        </div>

        {/* FAQ List */}
        <div className="max-w-4xl divide-y divide-[#E7E1D9]">
          {t.faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div key={index} className="group">
                <button
                  onClick={() => toggle(index)}
                  className="w-full flex items-start justify-between gap-6 py-7 text-left cursor-pointer"
                  aria-expanded={isOpen}
                >
                  <span
                    className={`text-base lg:text-lg font-medium leading-snug transition-colors duration-200 ${
                      isOpen ? 'text-[#F97700]' : 'text-[#0B0B0B] group-hover:text-[#F97700]'
                    }`}
                  >
                    {faq.question}
                  </span>
                  <span
                    className={`flex-shrink-0 w-7 h-7 flex items-center justify-center border transition-all duration-300 mt-0.5 ${
                      isOpen
                        ? 'border-[#F97700] bg-[#F97700] text-white rotate-45'
                        : 'border-[#E7E1D9] text-[#2E2E2E] group-hover:border-[#F97700] group-hover:text-[#F97700]'
                    }`}
                  >
                    <i className="ri-add-line text-base leading-none"></i>
                  </span>
                </button>

                {/* Answer — animated */}
                <div
                  className={`overflow-hidden transition-all duration-400 ease-in-out ${
                    isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                  style={{ transitionDuration: '350ms' }}
                >
                  <p className="pb-8 text-base text-[#2E2E2E] leading-relaxed pr-12">
                    {faq.answer}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="max-w-4xl mt-12 pt-12 border-t border-[#E7E1D9] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <p className="text-base text-[#2E2E2E]">
            {language === 'pt'
              ? 'Ainda tem dúvidas? Fale diretamente com nossa equipe.'
              : 'Still have questions? Speak directly with our team.'}
          </p>
          <a
            href="https://wa.me/5511930322408"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 border border-[#E7E1D9] text-[#0B0B0B] text-sm font-semibold hover:border-[#F97700] hover:text-[#F97700] transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-line text-base"></i>
            {language === 'pt' ? 'Falar no WhatsApp' : 'Chat on WhatsApp'}
          </a>
        </div>
      </div>
    </section>
  );
}